/**
 * useMargin — owns GET /api/v1/margin/{breakdown,timeline}, the reconciling drill through
 * the INVOICE fact. TanStack Query + the carried AuthContext token (CD-7: hooks fetch).
 *
 * The window is a month range on the INVOICE date, not the due date: that is the
 * periodisation that ties to Sage (re-bucketing the same lines on despatch reads +13.8%
 * against Sage where the invoice date reads −0.6%, because FM invoices in batches after it
 * ships). There is no `half` — every line here is billed.
 *
 * `placeholderData` keeps the previous level on screen while the next one loads, so a drill
 * repaints rather than blanking to a spinner.
 *
 * `enabled` exists because the surface lifted both queries above its tabs, to feed ONE set of
 * header cards. Only the tab on screen fetches; the other one waits until you open it.
 */

import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import type { MarginBreakdown, MarginTimeline } from '../types/margin'
import type { PathSegment, Site } from '../types/metrics'
import { ApiError, fetchApi } from './apiFetch'

function windowParams(site: Site, periodFrom: string | null, periodTo: string | null): URLSearchParams {
  const params = new URLSearchParams({ site })
  if (periodFrom && periodTo) {
    params.set('period_from', periodFrom)
    params.set('period_to', periodTo)
  }
  return params
}

/** Margin by invoice month — the trend, and the basis that reconciles to Sage. */
export function useMarginTimeline(
  periodFrom: string | null,
  periodTo: string | null,
  site: Site = 'Group',
  enabled = true
): UseQueryResult<MarginTimeline, ApiError> {
  const { token } = useAuth()
  const params = windowParams(site, periodFrom, periodTo)
  return useQuery<MarginTimeline, ApiError>({
    queryKey: ['margin-timeline', periodFrom, periodTo, site],
    queryFn: () => fetchApi<MarginTimeline>(`margin/timeline?${params.toString()}`, token),
    enabled: enabled && token !== null,
    placeholderData: keepPreviousData,
  })
}

export function useMargin(
  path: PathSegment[],
  periodFrom: string | null = null,
  periodTo: string | null = null,
  site: Site = 'Group',
  axis = 'customer',
  enabled = true
): UseQueryResult<MarginBreakdown, ApiError> {
  const { token } = useAuth()
  const pathJson = path.length ? JSON.stringify(path) : null
  const params = windowParams(site, periodFrom, periodTo)
  params.set('axis', axis)
  if (pathJson) params.set('path', pathJson)
  return useQuery<MarginBreakdown, ApiError>({
    queryKey: ['margin-breakdown', pathJson, periodFrom, periodTo, site, axis],
    queryFn: () => fetchApi<MarginBreakdown>(`margin/breakdown?${params.toString()}`, token),
    enabled: enabled && token !== null,
    placeholderData: keepPreviousData,
  })
}
